import React, { useEffect, useState } from 'react';
import { ClockIcon, ChevronDownIcon, ChevronRightIcon, EyeIcon } from 'lucide-react';
import { Order, OrderStatus, ORDER_STATUSES, STATUS_CONFIG } from './types';
import { Station, itemBelongsToStation, STATIONS } from './StationConfig';
import { StatusBadge } from './StatusBadge';
interface KitchenTicketViewProps {
  orders: Order[];
  onUpdateStatus: (orderId: string, status: OrderStatus) => void;
  onViewDetail: (order: Order) => void;
  activeStation: Station;
  getEffectiveStatus: (order: Order, station: Station) => OrderStatus;
}
// Helper component to auto-update time elapsed
const TimeElapsed = ({
  createdAt
}: {
  createdAt: string;
}) => {
  const [elapsed, setElapsed] = useState('');
  const [isOld, setIsOld] = useState(false);
  useEffect(() => {
    const updateTime = () => {
      const diff = Date.now() - new Date(createdAt).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1) {
        setElapsed('menos de 1 minuto');
      } else if (mins === 1) {
        setElapsed('1 minuto');
      } else if (mins < 60) {
        setElapsed(`${mins} minutos`);
      } else {
        const hrs = Math.floor(mins / 60);
        const remainMins = mins % 60;
        setElapsed(`${hrs}h ${remainMins}m`);
      }
      setIsOld(mins >= 15);
    };
    updateTime();
    const interval = setInterval(updateTime, 30000);
    return () => clearInterval(interval);
  }, [createdAt]);
  return <div className={`flex items-center gap-1.5 text-xs font-bold ${isOld ? 'text-red-400 animate-pulse' : 'text-gray-400'}`} data-id="element-167">
      <ClockIcon className="w-3.5 h-3.5" data-id="element-168" />
      <span data-id="element-169">Tiempo en cola: {elapsed}</span>
    </div>;
};
// Parse order_text into item blocks with their raw detail text
function parseOrderBlocks(orderText: string, station: Station): {
  name: string;
  rawDetails: string;
}[] {
  const lines = orderText.split('\n');
  const itemRegex = /x\d+\s*[—\-]\s*[₡$¢]/;
  const blocks: {
    name: string;
    rawDetails: string;
    belongsToStation: boolean;
  }[] = [];
  lines.forEach(line => {
    if (itemRegex.test(line)) {
      blocks.push({
        name: line.split(/[—\-]\s*[₡$¢]/)[0].trim(),
        rawDetails: '',
        belongsToStation: station === 'caja' || itemBelongsToStation(line, station)
      });
    } else if (blocks.length > 0) {
      const trimmed = line.trim();
      // Skip header/footer lines
      if (trimmed.length > 0 && !/^(Pedido|Entrega|Total|Cliente|Tel|Pago|Direcc)/i.test(trimmed)) {
        blocks[blocks.length - 1].rawDetails += (blocks[blocks.length - 1].rawDetails ? '\n' : '') + trimmed;
      }
    }
  });
  return blocks.filter(b => b.belongsToStation).map(({
    name,
    rawDetails
  }) => ({
    name,
    rawDetails
  }));
}
export function KitchenTicketView({
  orders,
  onUpdateStatus,
  onViewDetail,
  activeStation,
  getEffectiveStatus
}: KitchenTicketViewProps) {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const stationConfig = STATIONS.find(s => s.id === activeStation);
  const toggleItem = (key: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);else next.add(key);
      return next;
    });
  };
  if (orders.length === 0) {
    return <div className="flex flex-col items-center justify-center py-20 text-gray-500 bg-surface border border-gray-800 rounded-2xl border-dashed" data-id="element-170">
        <div className="text-4xl mb-4 opacity-50" data-id="element-171">{stationConfig?.icon}</div>
        <p className="text-lg font-medium" data-id="element-172">No hay pedidos pendientes</p>
        <p className="text-sm" data-id="element-173">Los nuevos pedidos aparecerán aquí</p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6" data-id="element-174">
      {orders.map((order, index) => {
      const effectiveStatus = getEffectiveStatus(order, activeStation);
      const itemBlocks = parseOrderBlocks(order.order_text, activeStation);
      // Status flow logic for quick advance button
      const flow: OrderStatus[] = ['new', 'accepted', 'preparing', 'ready', 'delivered'];
      const currentIdx = flow.indexOf(effectiveStatus);
      const nextStatus = currentIdx >= 0 && currentIdx < flow.length - 1 ? flow[currentIdx + 1] : null;
      const nextCfg = nextStatus ? STATUS_CONFIG[nextStatus] : null;
      return <div key={order.id} className="bg-surface flex flex-col rounded-2xl border border-gray-800 overflow-hidden shadow-xl relative" data-id="element-175">
            {/* Top Accent Line */}
            <div className={`h-1.5 w-full ${stationConfig?.bg.replace('/10', '') || 'bg-gray-700'}`} data-id="element-176" />

            {/* Header */}
            <div className="p-4 border-b border-gray-800 bg-surface-light/30 flex items-start justify-between gap-4" data-id="element-177">
              <div className="flex items-center gap-4" data-id="element-178">
                <div className={`flex items-center justify-center w-12 h-12 rounded-xl bg-surface-light border border-gray-700 shadow-inner ${stationConfig?.color || 'text-white'}`} data-id="element-179">
                  <span className="text-2xl font-black" data-id="element-180">{index + 1}</span>
                </div>
                <div data-id="element-181">
                  <div className="flex items-center gap-2 mb-1" data-id="element-182">
                    <span className="text-white font-mono font-bold text-lg" data-id="element-183">
                      {order.order_number.split('-').pop()}
                    </span>
                    {order.table_number && <span className="px-2 py-0.5 bg-dorado/15 text-dorado text-xs font-bold rounded border border-dorado/25" data-id="element-184">
                        Mesa {order.table_number}
                      </span>}
                  </div>
                  <TimeElapsed createdAt={order.created_at} data-id="element-185" />
                </div>
              </div>
              <button onClick={() => onViewDetail(order)} className="p-2 rounded-xl bg-surface-light border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 transition-colors" title="Ver detalle completo" data-id="element-186">
                <EyeIcon className="w-5 h-5" data-id="element-187" />
              </button>
            </div>

            {/* Items List — Accordion style */}
            <div className="p-3 flex-1 space-y-2 bg-[#1a1a1a]" data-id="element-188">
              {itemBlocks.map((block, i) => {
            const itemKey = `${order.id}_${i}`;
            const isExpanded = expandedItems.has(itemKey);
            const hasDetails = block.rawDetails.length > 0;
            return <div key={i} className="rounded-xl border border-gray-800 overflow-hidden" data-id="element-189">
                    {/* Item header — clickable */}
                    <button onClick={() => hasDetails && toggleItem(itemKey)} className={`w-full flex items-center gap-3 p-3 text-left transition-colors ${hasDetails ? 'hover:bg-surface-light/50 cursor-pointer' : 'cursor-default'} ${isExpanded ? 'bg-surface-light/30' : 'bg-surface/80'}`} data-id="element-190">
                      {hasDetails ? isExpanded ? <ChevronDownIcon className={`w-4 h-4 flex-shrink-0 ${stationConfig?.color || 'text-gray-400'}`} data-id="element-191" /> : <ChevronRightIcon className={`w-4 h-4 flex-shrink-0 ${stationConfig?.color || 'text-gray-400'}`} data-id="element-192" /> : <span className="w-4 h-4 flex-shrink-0" data-id="element-193" />}
                      <span className="text-base text-white font-black leading-tight flex-1" data-id="element-194">
                        {block.name}
                      </span>
                      {hasDetails && <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${stationConfig?.bg} ${stationConfig?.color}`} data-id="element-195">
                          SPECS
                        </span>}
                      {!hasDetails && <span className="text-[10px] text-gray-600 italic" data-id="element-196">
                          Simple
                        </span>}
                    </button>

                    {/* Expanded details */}
                    {isExpanded && hasDetails && <div className="px-4 pb-3 pt-1 bg-surface-light/20 border-t border-gray-800" data-id="element-197">
                        {block.rawDetails.split('\n').map((line, j) => {
                  const cleaned = line.replace(/^[·•\-\s]+/, '');
                  const colonIdx = cleaned.indexOf(':');
                  if (colonIdx > 0) {
                    const label = cleaned.substring(0, colonIdx).trim();
                    const value = cleaned.substring(colonIdx + 1).trim();
                    return <p key={j} className="text-sm leading-relaxed py-0.5" data-id="element-198">
                                <span className="text-gray-500" data-id="element-199">{label}:</span>{' '}
                                <span className="text-yellow-300 font-bold" data-id="element-200">
                                  {value}
                                </span>
                              </p>;
                  }
                  return <p key={j} className="text-sm text-gray-300 leading-relaxed py-0.5" data-id="element-201">
                              {cleaned}
                            </p>;
                })}
                      </div>}
                  </div>;
          })}

              {/* Raw order text — always available as fallback */}
              <details className="group" data-id="element-202">
                <summary className="text-[10px] text-gray-600 cursor-pointer hover:text-gray-400 transition-colors flex items-center gap-1 px-1 py-1" data-id="element-203">
                  <ChevronRightIcon className="w-3 h-3 group-open:rotate-90 transition-transform" data-id="element-204" />
                  Ver texto completo del pedido
                </summary>
                <pre className="text-[11px] text-gray-400 whitespace-pre-wrap font-mono bg-surface/60 rounded-lg p-2 mt-1 border border-gray-800 leading-relaxed max-h-48 overflow-y-auto" data-id="element-205">
                  {order.order_text}
                </pre>
              </details>
            </div>

            {/* Footer / Actions */}
            <div className="p-4 border-t border-gray-800 bg-surface-light/50 flex items-center justify-between relative" data-id="element-206">
              <div className="relative" data-id="element-207">
                <button onClick={() => setOpenDropdown(openDropdown === order.id ? null : order.id)} className="flex items-center gap-2 group bg-surface px-3 py-2 rounded-xl border border-gray-700 hover:border-gray-500 transition-colors" data-id="element-208">
                  <StatusBadge status={effectiveStatus} size="sm" data-id="element-209" />
                  <ChevronDownIcon className="w-4 h-4 text-gray-500 group-hover:text-gray-300" data-id="element-210" />
                </button>

                {openDropdown === order.id && <>
                    <div className="fixed inset-0 z-10" onClick={() => setOpenDropdown(null)} data-id="element-211" />
                    <div className="absolute bottom-full left-0 mb-2 z-20 bg-surface-light border border-gray-700 rounded-xl shadow-2xl py-1 min-w-[160px]" data-id="element-212">
                      {ORDER_STATUSES.map(s => {
                  const cfg = STATUS_CONFIG[s];
                  return <button key={s} onClick={() => {
                    onUpdateStatus(order.id, s);
                    setOpenDropdown(null);
                  }} className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 hover:bg-white/5 transition-colors ${effectiveStatus === s ? `${cfg.color} font-bold bg-white/5` : 'text-gray-400'}`} data-id="element-213">
                            <span className={`w-2 h-2 rounded-full ${cfg.dot}`} data-id="element-214" />
                            {cfg.label}
                            {effectiveStatus === s && <span className="ml-auto text-xs" data-id="element-215">✓</span>}
                          </button>;
                })}
                    </div>
                  </>}
              </div>

              {nextStatus && nextCfg && <button onClick={() => onUpdateStatus(order.id, nextStatus)} className={`px-5 py-2.5 rounded-xl text-sm font-bold border transition-all hover:scale-105 active:scale-95 shadow-lg ${nextCfg.bg} ${nextCfg.color}`} data-id="element-216">
                  {nextCfg.label} →
                </button>}
            </div>
          </div>;
    })}
    </div>;
}